// src/features/dashboard/settings/store.js
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "lib/api";
import { useAuthUser } from "lib/useAuthUser";
import { setStoredPeriodoId } from "../context";
import { DEFAULT_DASHBOARD_PORTAL, normalizeDashboardPortal } from "../portal";

const DashboardSettingsContext = React.createContext(null);

const LS_KEY = "dashboard.settings";
export const DASHBOARD_TINT_OPTIONS = [
  { value: "gray", label: "gray" },
  { value: "red", label: "red" },
  { value: "orange", label: "orange" },
  { value: "yellow", label: "yellow" },
  { value: "green", label: "green" },
  { value: "teal", label: "teal" },
  { value: "blue", label: "blue" },
  { value: "cyan", label: "cyan" },
  { value: "purple", label: "purple" },
  { value: "pink", label: "pink" },
];
const DASHBOARD_TINT_SET = new Set(DASHBOARD_TINT_OPTIONS.map((x) => x.value));

function loadLS() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "{}");
  } catch {
    return {};
  }
}
function saveLS(obj) {
  localStorage.setItem(LS_KEY, JSON.stringify(obj));
}

function clearLS() {
  localStorage.removeItem(LS_KEY);
}

function sanitizeTint(tint) {
  if (tint === "slate" || tint === "pizarra" || tint === "pizzarra") return "gray";
  if (tint === "emerald") return "green";
  if (tint === "violet") return "purple";
  if (tint === "amber") return "orange";
  return DASHBOARD_TINT_SET.has(tint) ? tint : "gray";
}

function findPeriodoId(periodos, nivelId, semestreId) {
  const p = periodos.find((x) => x.nivelId === nivelId && x.semestreId === semestreId);
  return p ? p.id : "";
}

function semestresForNivel(periodos, semestres, nivelId) {
  const semestreIds = new Set(periodos.filter((p) => p.nivelId === nivelId).map((p) => p.semestreId));
  return semestres.filter((s) => semestreIds.has(s.id));
}

function uniqPortals(list) {
  return Array.from(
    new Set(
      (Array.isArray(list) ? list : [])
        .map((portal) => normalizeDashboardPortal(portal))
        .filter(Boolean)
    )
  );
}

export function DashboardSettingsProvider({ children, requestedPortal }) {
  const user = useAuthUser();

  const q = useQuery({
    queryKey: ["dashboard", "context", user?.uid ?? "anon"],
    queryFn: () => api("/dashboard/context"),
    enabled: user !== undefined && !!user,
    staleTime: 10 * 60_000,
    retry: (count, err) => err?.status !== 401 && err?.status !== 403 && count < 2,
    refetchOnWindowFocus: false,
  });

  const [state, setState] = React.useState(() => loadLS());
  const patchState = React.useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      saveLS(next);
      return next;
    });
  }, []);

  const value = React.useMemo(() => {
    const dashboardTint = sanitizeTint(state.dashboardTint);
    const requested = normalizeDashboardPortal(requestedPortal);

    const reset = () => {
      clearLS();
      setStoredPeriodoId("");
      setState({});
    };

    const base = {
      dashboardTint,
      setDashboardTint: (nextTint) => patchState({ dashboardTint: sanitizeTint(nextTint) }),
      reset,
      refresh: q.refetch,
    };

    // Firebase resolved and no user: never expose stale data
    if (user === null) {
      return { ...base, ready: false, loading: false, error: null };
    }

    // Firebase still loading
    if (user === undefined) {
      return { ...base, ready: false, loading: true, error: null };
    }

    // Backend says forbidden => no dashboard portal access
    if (q.error?.status === 403) {
      const payload = q.error?.payload || {};
      return {
        ...base,
        ready: false,
        loading: false,
        error: q.error,
        denied: true,
        denyCode: payload.error || "FORBIDDEN",
        requiredRole:
          payload.role ||
          (Array.isArray(payload.roles) && payload.roles.length ? payload.roles[0] : undefined),
        portal: requested || DEFAULT_DASHBOARD_PORTAL,
        availablePortals: [],
        fallbackPortal: null,
      };
    }

    if (!q.data) {
      return { ...base, loading: q.isLoading, error: q.error, ready: false };
    }

    const availablePortals = uniqPortals(q.data?.portals);
    const fallbackPortal = availablePortals[0] || null;
    const activePortal = requested || fallbackPortal || DEFAULT_DASHBOARD_PORTAL;

    if (!availablePortals.length) {
      return {
        ...base,
        ready: false,
        loading: false,
        error: null,
        denied: true,
        denyCode: "ROLE_REQUIRED",
        portal: activePortal,
        availablePortals,
        fallbackPortal: null,
      };
    }

    if (requested && !availablePortals.includes(requested)) {
      return {
        ...base,
        ready: false,
        loading: false,
        error: null,
        denied: true,
        denyCode: "PORTAL_REQUIRED",
        portal: activePortal,
        availablePortals,
        fallbackPortal,
      };
    }

    const me = q.data?.me ?? null;
    const rolesByPortal = q.data?.rolesByPortal ?? {};

    if (activePortal === "camp") {
      return {
        ...base,
        ready: true,
        loading: false,
        error: null,
        denied: false,
        portal: "camp",
        availablePortals,
        fallbackPortal,
        rolesByPortal,
        me,
        periodoId: "",
        nivelId: "",
        semestreId: "",
        campusId: "",
        options: {
          campuses: [],
          niveles: [],
          nivelesForCampus: [],
          semestresForNivel: [],
          tintOptions: DASHBOARD_TINT_OPTIONS,
        },
        setCampusId: () => {},
        setNivelId: () => {},
        setSemestreId: () => {},
      };
    }

    const clubContext = q.data?.club;
    if (!clubContext?.options || !clubContext?.current) {
      return {
        ...base,
        ready: false,
        loading: false,
        error: null,
        denied: true,
        denyCode: "CLUB_CONTEXT_MISSING",
        portal: "club",
        availablePortals,
        fallbackPortal,
      };
    }

    const { options, current } = clubContext;
    const campuses = options.campuses || [];
    const niveles = options.niveles || [];
    const semestres = options.semestres || [];
    const periodos = options.periodos || [];

    const defaultNivelId = current.nivelId;
    const defaultSemestreId = current.semestreId;

    let nivelId = state.nivelId && niveles.some((n) => n.id === state.nivelId) ? state.nivelId : defaultNivelId;
    let campusId = niveles.find((n) => n.id === nivelId)?.campusId || current.campusId;

    const semsForNivel = semestresForNivel(periodos, semestres, nivelId);
    let semestreId =
      state.semestreId && semsForNivel.some((s) => s.id === state.semestreId)
        ? state.semestreId
        : semsForNivel.find((s) => s.id === defaultSemestreId)?.id || semsForNivel[0]?.id || defaultSemestreId;

    const periodoId = findPeriodoId(periodos, nivelId, semestreId);
    const nivelesForCampus = niveles.filter((n) => n.campusId === campusId);

    if (!nivelesForCampus.some((n) => n.id === nivelId)) {
      const fallbackNivel = nivelesForCampus[0] || niveles[0];
      if (fallbackNivel) {
        nivelId = fallbackNivel.id;
        campusId = fallbackNivel.campusId;
        const sems = semestresForNivel(periodos, semestres, nivelId);
        semestreId = sems[0]?.id || semestreId;
      }
    }

    const actions = {
      setCampusId: (nextCampusId) => {
        const nivs = niveles.filter((n) => n.campusId === nextCampusId);
        const nextNivel = nivs[0];
        if (!nextNivel) return;

        const sems = semestresForNivel(periodos, semestres, nextNivel.id);
        const nextSem = sems.find((s) => s.id === semestreId)?.id || sems[0]?.id || semestreId;

        patchState({ campusId: nextCampusId, nivelId: nextNivel.id, semestreId: nextSem });
      },

      setNivelId: (nextNivelId) => {
        const nextNivel = niveles.find((n) => n.id === nextNivelId);
        if (!nextNivel) return;

        const sems = semestresForNivel(periodos, semestres, nextNivelId);
        const nextSem = sems.find((s) => s.id === semestreId)?.id || sems[0]?.id || semestreId;

        patchState({ campusId: nextNivel.campusId, nivelId: nextNivelId, semestreId: nextSem });
      },

      setSemestreId: (nextSemestreId) => {
        const sems = semestresForNivel(periodos, semestres, nivelId);
        if (!sems.some((s) => s.id === nextSemestreId)) return;

        patchState({ campusId, nivelId, semestreId: nextSemestreId });
      },
    };

    return {
      ...base,
      ready: true,
      loading: false,
      error: null,
      denied: false,
      portal: "club",
      availablePortals,
      fallbackPortal,
      rolesByPortal,
      me,
      periodoId,
      nivelId,
      semestreId,
      campusId,
      options: {
        campuses,
        niveles,
        nivelesForCampus,
        semestresForNivel: semsForNivel,
        tintOptions: DASHBOARD_TINT_OPTIONS,
      },
      ...actions,
    };
  }, [requestedPortal, user, q.data, q.error, q.isLoading, q.refetch, state, patchState]);

  return <DashboardSettingsContext.Provider value={value}>{children}</DashboardSettingsContext.Provider>;
}

export function useDashboardSettings() {
  const ctx = React.useContext(DashboardSettingsContext);
  if (!ctx) throw new Error("useDashboardSettings must be used inside DashboardSettingsProvider");
  return ctx;
}
